import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';

import { TeacherInterviewVideoUrlQuery } from 'core/operations/queries/teacherInterviews';

import {
  ErpInterviewZoomCandidatesQuery,
  ErpTeacherInterviewsQuery,
  Maybe,
  useAttachInterviewFromZoomMutation,
  useAttachInterviewLinkMutation,
  useErpInterviewZoomCandidatesQuery,
  useErpInterviewZoomDefaultAccountQuery,
  useErpTeacherInterviewsQuery,
  useErpTeacherInterviewTranscriptQuery,
  useRefreshInterviewZoomMutation,
  useRemoveInterviewMutation,
  useRetryInterviewDownloadMutation,
  useSaveInterviewTranscriptMutation,
  useUploadInterviewVideoMutation,
} from 'core/graphql';

export type ITeacherInterview = ErpTeacherInterviewsQuery['teacherInterviews'][number];
export type IInterviewZoomCandidate = ErpInterviewZoomCandidatesQuery['interviewZoomCandidates'][number];

/** Lowercase strings on the wire (`teachers.interview_constants`). */
export const INTERVIEW_SOURCES = ['zoom', 'upload', 'link'] as const;
export type InterviewSource = typeof INTERVIEW_SOURCES[number];

export const INTERVIEW_STATUSES = [
  'new',
  'downloading',
  'ready',
  'failed',
] as const;
export type InterviewStatus = typeof INTERVIEW_STATUSES[number];

export const INTERVIEW_STATUS_COLOR: Record<InterviewStatus, string> = {
  new: 'default',
  downloading: 'blue',
  ready: 'green',
  failed: 'red',
};

/** Backend `InterviewServiceError` codes the UI translates explicitly. */
const INTERVIEW_ERROR_CODES = [
  'NOT_FOUND',
  'INVALID_INPUT',
  'ALREADY_ATTACHED',
  'FILE_TOO_LARGE',
  'UNSUPPORTED_FILE',
  'ZOOM_UNAVAILABLE',
  'WINDOW_TOO_WIDE',
  'COOLDOWN',
  'NO_TRANSCRIPT',
] as const;

interface IInterviewCustomError {
  code?: Maybe<string>;
  message?: Maybe<string>;
}

export const interviewErrorMessage = (
  t: (key: string) => string,
  customErrors?: Maybe<Array<Maybe<IInterviewCustomError>>>,
): string => {
  const first = (customErrors || []).find(Boolean);
  const code = first?.code || '';
  if ((INTERVIEW_ERROR_CODES as readonly string[]).includes(code)) {
    return t(`pages.teacherDetail.hiring.errors.${code}`);
  }
  return first?.message || t('pages.teacherDetail.hiring.errors.generic');
};

interface IActionResult {
  success: boolean;
  errorMessage?: string;
}

/**
 * Spec 0028 B — the teacher's hiring interviews.
 *
 * Every mutation refetches `ErpTeacherInterviews`, because a Zoom attach flips
 * the row's status asynchronously (the artifact pull runs in Celery) and the
 * list is the only place that shows it.
 */
export const useTeacherInterviews = (teacherId?: string) => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useErpTeacherInterviewsQuery({
    // `|| ''` rather than a cast: an empty required ID still satisfies the
    // contract, whereas `undefined` goes out as no variable and 400s — and this
    // query IS refetched by name.
    variables: { teacherId: teacherId || '' },
    skip: !teacherId,
    fetchPolicy: 'cache-and-network',
  });

  const refetchList = { refetchQueries: ['ErpTeacherInterviews'] };
  const [attachZoomRun] = useAttachInterviewFromZoomMutation(refetchList);
  const [attachLinkRun] = useAttachInterviewLinkMutation(refetchList);
  const [uploadRun] = useUploadInterviewVideoMutation(refetchList);
  // ⚠️ `ErpTeacherInterviewTranscript` is deliberately NOT refetched by name.
  // Refetch-by-name re-runs EVERY registered observable with that name,
  // including the `skip`ped instance the drawer leaves behind with no
  // variables — that request goes out as `{}`, the backend rejects it with
  // «Variable '$interviewId' of required type 'ID!' was not provided», and
  // errorLink turns the 400 into a redirect to /500 on a mutation that
  // actually SUCCEEDED. The drawer refetches its own query instead, with the
  // variables it is bound to.
  const [saveTranscriptRun] = useSaveInterviewTranscriptMutation(refetchList);
  const [retryRun] = useRetryInterviewDownloadMutation(refetchList);
  const [removeRun] = useRemoveInterviewMutation(refetchList);
  const [pending, setPending] = useState(false);
  const client = useApolloClient();

  /**
   * Fetch the signed video URL ON CLICK, never as part of the list payload:
   * `AWS_QUERYSTRING_EXPIRE` makes it short-lived, and the backend writes an
   * audit row on every issue — caching it in a list would both rot and lie about
   * who looked.
   */
  const getVideoUrl = useCallback(
    async (interviewId: string): Promise<string | undefined> => {
      const result = await client.query({
        query: TeacherInterviewVideoUrlQuery,
        variables: { interviewId },
        fetchPolicy: 'network-only',
      });
      return result.data?.teacherInterviewVideoUrl || undefined;
    },
    [client],
  );

  const run = useCallback(
    async (
      call: () => Promise<
        | {
            success?: boolean | null;
            customErrors?: Maybe<Array<Maybe<IInterviewCustomError>>>;
          }
        | null
        | undefined
      >,
    ): Promise<IActionResult> => {
      setPending(true);
      try {
        const payload = await call();
        if (payload?.success) return { success: true };
        return {
          success: false,
          errorMessage: interviewErrorMessage(t, payload?.customErrors),
        };
      } catch (e) {
        // PermissionDenied lands in GraphQL `errors`, so the promise REJECTS.
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setPending(false);
      }
    },
    [t],
  );

  const attachFromZoom = useCallback(
    (zoomRecordingId: string, title?: string) =>
      run(async () => {
        const result = await attachZoomRun({
          variables: { teacherId: teacherId as string, zoomRecordingId, title },
        });
        return result.data?.attachTeacherInterviewZoom;
      }),
    [run, attachZoomRun, teacherId],
  );

  const attachLink = useCallback(
    (url: string, title?: string) =>
      run(async () => {
        const result = await attachLinkRun({
          variables: { teacherId: teacherId as string, url, title },
        });
        return result.data?.attachTeacherInterviewLink;
      }),
    [run, attachLinkRun, teacherId],
  );

  const uploadVideo = useCallback(
    (file: File, title?: string) =>
      run(async () => {
        // The raw File goes straight into variables — apollo-upload-client's
        // createUploadLink switches to multipart when it sees one. There is no
        // byte progress on a multipart fetch, hence the indeterminate spinner.
        const result = await uploadRun({
          variables: { teacherId: teacherId as string, file, title },
        });
        return result.data?.uploadTeacherInterviewVideo;
      }),
    [run, uploadRun, teacherId],
  );

  const saveTranscript = useCallback(
    (interviewId: string, text: string) =>
      run(async () => {
        const result = await saveTranscriptRun({
          variables: { interviewId, text },
        });
        return result.data?.setTeacherInterviewTranscript;
      }),
    [run, saveTranscriptRun],
  );

  const retryDownload = useCallback(
    (interviewId: string) =>
      run(async () => {
        const result = await retryRun({ variables: { interviewId } });
        return result.data?.retryTeacherInterviewDownload;
      }),
    [run, retryRun],
  );

  const remove = useCallback(
    (interviewId: string) =>
      run(async () => {
        const result = await removeRun({ variables: { interviewId } });
        return result.data?.deleteTeacherInterview;
      }),
    [run, removeRun],
  );

  return {
    interviews: data?.teacherInterviews || [],
    loading,
    pending,
    refetch,
    attachFromZoom,
    attachLink,
    uploadVideo,
    saveTranscript,
    retryDownload,
    remove,
    getVideoUrl,
  };
};

interface IUseZoomCandidatesArgs {
  dateFrom?: string;
  dateTo?: string;
  accountId?: string;
  search?: string;
  skip?: boolean;
}

/**
 * The picker's data. Backed by `interviewZoomCandidates` — a narrow query in the
 * teachers schema, NOT `allZoomRecordings`: that one is gated on the Zoom roles
 * (no HR, yes Methodist — the wrong set here) and exposes raw download URLs.
 */
export const useInterviewZoomCandidates = ({
  dateFrom,
  dateTo,
  accountId,
  search,
  skip = false,
}: IUseZoomCandidatesArgs) => {
  const { t } = useTranslation();
  const { data, loading, refetch } = useErpInterviewZoomCandidatesQuery({
    variables: { dateFrom, dateTo, accountId, search: search || undefined },
    skip,
    fetchPolicy: 'cache-and-network',
  });
  const { data: defaults } = useErpInterviewZoomDefaultAccountQuery({ skip });
  const [refreshRun] = useRefreshInterviewZoomMutation();
  const [refreshing, setRefreshing] = useState(false);

  /** Pull a window straight from Zoom — the scheduled sync often never fires
   *  for an interview recorded outside a group's lesson slot. */
  const refreshFromZoom = useCallback(
    async (from: string, to: string) => {
      setRefreshing(true);
      try {
        const result = await refreshRun({
          variables: { dateFrom: from, dateTo: to },
        });
        const payload = result.data?.refreshInterviewZoomRecordings;
        if (!payload?.success) {
          return {
            success: false,
            errorMessage: interviewErrorMessage(t, payload?.customErrors),
          };
        }
        await refetch();
        return { success: true, result: payload.result || undefined };
      } catch (e) {
        return { success: false, errorMessage: (e as Error).message };
      } finally {
        setRefreshing(false);
      }
    },
    [refreshRun, refetch, t],
  );

  return {
    candidates: data?.interviewZoomCandidates || [],
    defaultAccountId: defaults?.interviewZoomDefaultAccountId || undefined,
    loading,
    refreshing,
    refreshFromZoom,
  };
};

/**
 * The transcript body, loaded only when the drawer opens.
 *
 * `refetch` is returned so the drawer can reload ITS OWN query after a save —
 * see the comment on `saveTranscriptRun` for why refetching this one by name
 * from the mutation is a 400.
 */
export const useInterviewTranscript = (interviewId?: string) => {
  const { data, loading, refetch } = useErpTeacherInterviewTranscriptQuery({
    variables: { interviewId: interviewId as string },
    skip: !interviewId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    transcript: data?.teacherInterviewTranscript || undefined,
    loading,
    refetch,
  };
};
